import { Bike } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import type {
  CreditStandingResponse,
  PaymentMethodSummary,
} from '@aeris/contracts'
import {
  MONEY_SCALE,
  formatMoney,
  isPositive,
  parseHumanAmount,
  toString,
  type Decimal,
  add,
  compare,
  fromString,
  zero,
} from '@aeris/money'
import {
  PAYMENT_METHOD_KIND_LABELS,
  settlementOf,
  type PaymentMethodKind,
} from '@aeris/payments'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  MoneyInput,
  cn,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  useScopedKeydown,
} from '@aeris/ui'

import type { CustomerLookup } from '../lib/customer-lookup.ts'

import { AuthorisationDialog } from './authorisation-dialog.tsx'

/**
 * Recebimento — the second half of a counter sale.
 *
 * Mixed payment is the ordinary case, not the exotic one: section 21 says so,
 * and R$ 150,00 as R$ 50,00 in cash and R$ 100,00 on a card is what actually
 * happens. So this is a LIST of tenders rather than one method and one amount,
 * and it is driven from the keyboard: a digit picks the method, the amount is
 * already focused and pre-filled with what is still owed, Enter adds it, and
 * Enter again on a settled sale finishes.
 *
 * Every figure here — paid, still owed, change — comes from `settlementOf` in
 * `@aeris/payments`, which is the same function the server runs when it decides
 * whether to complete the sale. Change in particular is not `paid - total`:
 * change comes out of the drawer, so it can never exceed the cash that went
 * into it, and a PIX of R$ 100,00 against a bill of R$ 90,00 produces no change
 * at all. Reimplementing that here would eventually hand out money the till
 * never received.
 */

export type PosTender = {
  readonly key: string
  readonly methodId: string
  readonly methodName: string
  readonly methodKind: PaymentMethodKind
  readonly amount: Decimal
}

export type PosPaymentDialogProps = {
  total: Decimal
  methods: readonly PaymentMethodSummary[]
  /**
   * The customer's credit, when one is identified.
   *
   * Shown the moment a fiado method is selected, so the operator learns the
   * limit BEFORE ringing anything up. The server decides — this is the same
   * figure it will decide against, fetched a moment earlier.
   */
  credit: CreditStandingResponse | null
  /**
   * O saldo de vale do cliente (F126), ou nulo quando não há cliente.
   *
   * Mostrado quando "Vale" é escolhido, e usado para recusar aqui mesmo um vale
   * maior que o saldo — o servidor recusa igual, mas o operador descobrir no
   * Concluir, com o cliente esperando, é pior.
   */
  voucherBalance?: string | null | undefined
  /**
   * Por que o saldo de vale não veio, quando não veio (F208). Ausente, saldo
   * nulo é "sem cliente" — o comportamento de antes.
   */
  voucherLookup?: CustomerLookup | undefined
  /** O mesmo, para o crédito do fiado (F208). */
  creditLookup?: CustomerLookup | undefined
  pending: boolean
  error: string | null
  /**
   * `creditAuthorisationId` é a senha do gerente liberando o fiado com
   * vencido (F293); nulo quando ninguém precisou liberar.
   */
  onConfirm: (
    tenders: readonly PosTender[],
    creditAuthorisationId: string | null
  ) => void
  onClose: () => void
  /**
   * A saída para a entrega, quando ninguém sabe ainda como vão pagar (F105).
   *
   * *"a caixa cria a venda, o motoboy entrega e depois que ele volta é que ela
   * sabe como o cara pagou"*. Até aqui este diálogo era uma sala com uma porta
   * para a frente, e a porta pedia dinheiro: quem clicou em Finalizar tinha de
   * saber que se sai com Esc e que existe outro botão embaixo.
   *
   * Fecha o recebimento e abre a pergunta do entregador. Ausente esconde a
   * saída inteira — o balcão sem entrega não precisa saber que ela existe.
   */
  onDeliver?: (() => void) | undefined
  /**
   * Por que a entrega não dá, quando não dá (F105).
   *
   * Vira FRASE, e nunca botão cinza. Dentro de um modal o F4 é inalcançável,
   * então a razão tem de dizer o caminho de volta — um botão desabilitado aqui
   * seria a promessa que a F101 recusou, agravada por estar num lugar de onde
   * não dá para cumpri-la.
   */
  deliveryBlocked?: string | null | undefined
  /**
   * O sinal de um pedido (F173): parte do `total`, nunca mais que ele.
   *
   * O campo começa vazio (o comum é o cliente mandar uma parte, não o total),
   * conclui com qualquer valor acima de zero, e não aceita passar do que falta
   * — sinal não dá troco. Quem chama tira da lista as formas que geram
   * recebível.
   */
  deposit?: boolean | undefined
}

let sequence = 0

/** A frase do erro ao lançar, para cada motivo de o saldo não estar aqui. */
const lookupProblem = (
  lookup: CustomerLookup,
  noCustomer: string,
  what: string
): string => {
  switch (lookup.state) {
    case 'loading':
      return `Ainda consultando ${what} do cliente. Aguarde um instante.`
    case 'forbidden':
      return `Seu usuário não pode consultar ${what}. Peça ao gerente.`
    case 'error':
      return `Não foi possível ler ${what} do cliente. Tente de novo.`
    case 'no-customer':
    case 'ready':
      return noCustomer
  }
}

/**
 * O aviso quando o saldo não está na tela (F208). Só "sem cliente" manda
 * escolher o cliente — os outros dizem o que de fato aconteceu.
 */
const LookupNotice = ({
  lookup,
  what,
  permission,
  children,
}: {
  lookup: CustomerLookup
  what: string
  permission: string
  children: ReactNode
}): ReactNode => {
  const warning =
    'rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) px-3 py-2 text-(length:--text-xs) text-(--text-primary)'
  switch (lookup.state) {
    case 'loading':
      return (
        <p
          role="status"
          className="rounded-(--radius-md) border border-(--border-subtle) px-3 py-2 text-(length:--text-xs) text-(--text-secondary)"
        >
          Consultando {what} do cliente…
        </p>
      )
    case 'forbidden':
      return (
        <p className={warning}>
          Seu usuário não pode consultar {what} (acesso a {permission}). Peça ao
          gerente para liberar, ou receba por outra forma.
        </p>
      )
    case 'error':
      return (
        <p
          role="alert"
          className="flex flex-wrap items-center gap-2 rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
        >
          Não foi possível ler {what} do cliente.
          <Button size="sm" variant="secondary" onClick={lookup.retry}>
            Tentar de novo
          </Button>
        </p>
      )
    case 'no-customer':
    case 'ready':
      return <p className={warning}>{children}</p>
  }
}

/**
 * Os atalhos do recebimento, montados DENTRO do diálogo — F138.
 *
 * Um componente só para isto porque o lugar onde o atalho é registrado decide
 * de quem ele é. No Modo PC o recebimento é uma janela filha do PDV, e o PDV
 * fica bloqueado enquanto ela está aberta: registrado no corpo do diálogo, lá
 * em cima, o atalho perguntaria ao PDV se a tecla é dele — a resposta seria
 * não, e o F2 deixaria de concluir a venda justamente na janela onde ela é
 * concluída.
 */
const PaymentKeys = ({
  onKeyDown,
}: {
  onKeyDown: (event: KeyboardEvent) => void
}): null => {
  useScopedKeydown(onKeyDown)
  return null
}

export const PosPaymentDialog = ({
  total,
  methods,
  credit,
  voucherBalance = null,
  voucherLookup,
  creditLookup,
  pending,
  error,
  onConfirm,
  onClose,
  onDeliver,
  deliveryBlocked = null,
  deposit = false,
}: PosPaymentDialogProps): ReactNode => {
  const [tenders, setTenders] = useState<PosTender[]>([])
  const [methodId, setMethodId] = useState(methods[0]?.id ?? '')
  /**
   * What the operator typed, or null while the field is following the total.
   *
   * Two states rather than one, because "the field shows what is still owed"
   * and "the field shows what somebody typed" are different facts. Collapsing
   * them into one string forces an effect to write state on every render — and
   * an effect that calls setState is a cascade of renders on the one screen
   * that must not stutter.
   */
  const [typed, setTyped] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  /** F293: a liberação do gerente para o fiado com vencido. */
  const [creditRelease, setCreditRelease] = useState<{
    id: string
    by: string
  } | null>(null)
  const [askingRelease, setAskingRelease] = useState(false)

  const amountRef = useRef<HTMLInputElement>(null)
  /** Kept in a ref so the window listener below never closes over a stale one. */
  const confirmRef = useRef<() => void>(() => undefined)
  /** Pelo mesmo motivo: o listener vive uma vez e a prop muda (F105). */
  const deliverRef = useRef<(() => void) | null>(null)

  /** The method the operator is about to tender with. */
  const selected = methods.find((entry) => entry.id === methodId)

  const settlement = useMemo(
    () =>
      settlementOf(
        total,
        tenders.map((tender) => ({
          direction: 'in' as const,
          amount: tender.amount,
          methodKind: tender.methodKind,
          status: 'settled' as const,
        }))
      ),
    [total, tenders]
  )

  /*
   * No sinal, "pronto" é ter recebido alguma coisa sem passar do que falta. Na
   * venda e na retirada, é não faltar nada — e total zero (o sinal já cobriu
   * o pedido) está pronto sem pagamento nenhum.
   */
  const settled = deposit
    ? isPositive(settlement.paid) && !isPositive(settlement.excess)
    : settlement.outcome !== 'short'

  /**
   * The amount field starts on what is still owed.
   *
   * The overwhelmingly common sale is one method for the whole bill, and making
   * the operator type a figure the till already knows is the difference between
   * two keystrokes and eleven. Derived rather than stored, so it follows the
   * outstanding figure until somebody disagrees with it.
   */
  const amountValue =
    typed ??
    (!deposit && isPositive(settlement.outstanding)
      ? toString(settlement.outstanding)
      : '')

  useEffect(() => {
    amountRef.current?.focus()
    amountRef.current?.select()
  }, [tenders.length])

  /**
   * F2 concludes from anywhere in the dialog.
   *
   * The same key that opened it, which is what makes the whole sale one
   * sequence an operator can perform without looking: F2, amount, Enter, F2.
   *
   * Escutado por `PaymentKeys`, dentro do `DialogContent` (F138).
   */
  const onPaymentKey = (event: KeyboardEvent): void => {
    if (event.key === 'F2') {
      event.preventDefault()
      confirmRef.current()
      return
    }

    /*
     * F10 aqui dentro, e não pelo teclado global do PDV (F105).
     *
     * O global se desliga enquanto este diálogo está aberto, de propósito:
     * quem digita um valor não pode disparar atalhos de carrinho. Então a
     * tecla que leva à entrega mora onde a tecla que conclui a venda já
     * mora, e as duas seguem a mesma regra.
     */
    if (event.key === 'F10') {
      event.preventDefault()
      deliverRef.current?.()
      return
    }

    /*
     * Alt, not the bare digit.
     *
     * The old select labelled its options "1 · Dinheiro", which promised a
     * digit shortcut that was never wired. A bare digit cannot be wired: the
     * amount field holds the focus and is made of digits, so `1` would mean
     * "one centavo" and "Dinheiro" at the same time. Alt is free, and it is
     * printed on each button rather than left to be discovered.
     */
    if (!event.altKey || event.ctrlKey || event.metaKey) return
    const position = Number.parseInt(event.key, 10)
    if (!Number.isInteger(position) || position < 1) return
    const method = methods[position - 1]
    if (method === undefined) return
    event.preventDefault()
    setMethodId(method.id)
  }

  const addTender = (): void => {
    const method = methods.find((entry) => entry.id === methodId)
    if (method === undefined) {
      setProblem('Escolha uma forma de pagamento.')
      return
    }

    const amount = parseHumanAmount(amountValue, MONEY_SCALE)
    if (amount === null || !isPositive(amount)) {
      setProblem('Informe um valor maior que zero.')
      return
    }

    if (method.kind === 'voucher') {
      if (voucherBalance === null) {
        setProblem(
          lookupProblem(
            voucherLookup ?? { state: 'no-customer' },
            'O vale é o saldo de um cliente. Identifique o cliente antes de usar o vale.',
            'o saldo de vale'
          )
        )
        return
      }
      const balance = fromString(voucherBalance, MONEY_SCALE)
      const alreadyOnVoucher = tenders
        .filter((tender) => tender.methodKind === 'voucher')
        .reduce((total, tender) => add(total, tender.amount), zero(MONEY_SCALE))
      if (compare(add(alreadyOnVoucher, amount), balance) > 0) {
        setProblem(
          `O saldo de vale deste cliente é ${formatMoney(balance)}. Receba o restante por outra forma.`
        )
        return
      }
    }

    sequence += 1
    setProblem(null)
    // Back to following the outstanding figure, which is what the next tender
    // is for in a mixed payment.
    setTyped(null)
    setTenders((current) => [
      ...current,
      {
        key: `tender-${String(sequence)}`,
        methodId: method.id,
        methodName: method.name,
        methodKind: method.kind,
        amount,
      },
    ])
  }

  const confirm = (): void => {
    if (tenders.length === 0 && (deposit || isPositive(total))) {
      setProblem('Informe ao menos um pagamento.')
      return
    }
    if (!settled) {
      setProblem(
        deposit
          ? 'O sinal não pode passar do que falta pagar.'
          : 'Ainda falta receber.'
      )
      return
    }
    onConfirm(tenders, creditRelease?.id ?? null)
  }

  // Written in an effect, never during render: a ref updated while rendering is
  // a value React is allowed to discard.
  useEffect(() => {
    confirmRef.current = confirm
    /*
     * Nulo quando a entrega não pode acontecer, e não só quando não foi
     * oferecida: F10 tem de fazer o mesmo que o botão faz, senão o atalho é
     * uma porta que a tela diz estar fechada.
     */
    deliverRef.current =
      onDeliver === undefined || deliveryBlocked !== null ? null : onDeliver
  })

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={deposit ? 'Receber sinal' : 'Recebimento'}
        description={
          deposit
            ? 'Escolha a forma, informe quanto o cliente pagou agora e pressione Enter. F2 grava o sinal.'
            : 'Escolha a forma, informe o valor e pressione Enter. F2 conclui a venda.'
        }
        className="max-w-2xl"
      >
        <PaymentKeys onKeyDown={onPaymentKey} />
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(
              [
                [deposit ? 'Falta pagar' : 'Total', settlement.total],
                [deposit ? 'Sinal' : 'Recebido', settlement.paid],
                [deposit ? 'Fica faltando' : 'Falta', settlement.outstanding],
                deposit
                  ? (['Passou', settlement.excess] as const)
                  : (['Troco', settlement.changeDue] as const),
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="flex flex-col">
                <span className="text-(length:--text-xs) text-(--text-muted)">
                  {label}
                </span>
                <output
                  aria-label={label}
                  aria-live="polite"
                  className="font-(--font-mono) text-(length:--text-lg) text-(--text-primary)"
                >
                  {formatMoney(value)}
                </output>
              </div>
            ))}
          </div>

          {/* Money that arrived over the odds and cannot come back out of the
              drawer, because it never went in. Naming it is the only honest
              option: reporting it as change would make the till short. */}
          {isPositive(settlement.changeRefused) ? (
            <p
              role="status"
              className="text-(length:--text-sm) text-(--text-secondary)"
            >
              {formatMoney(settlement.changeRefused)} a mais que não pode ser
              devolvido em dinheiro, porque não entrou em dinheiro.
            </p>
          ) : null}

          {/* O saldo de vale, quando "Vale" está escolhido (F126). */}
          {selected?.kind === 'voucher' ? (
            voucherBalance === null ? (
              <LookupNotice
                lookup={voucherLookup ?? { state: 'no-customer' }}
                what="o saldo de vale"
                permission="Trocas e vales"
              >
                O vale é o saldo de um cliente. Escolha o cliente antes de usar
                esta forma de pagamento.
              </LookupNotice>
            ) : (
              <p className="rounded-(--radius-md) border border-(--border-subtle) px-3 py-2 text-(length:--text-xs) text-(--text-secondary)">
                Saldo de vale do cliente:{' '}
                <strong className="font-(--font-mono) text-(--text-primary)">
                  {formatMoney(fromString(voucherBalance, MONEY_SCALE))}
                </strong>
              </p>
            )
          ) : null}

          {/* Shown the moment a fiado method is selected, and only then: the
              operator learns the ceiling before ringing anything up rather
              than meeting it as a refusal after. */}
          {selected?.generatesReceivable === true ? (
            credit === null ? (
              <LookupNotice
                lookup={creditLookup ?? { state: 'no-customer' }}
                what="o crédito"
                permission="Financeiro"
              >
                Venda a prazo precisa de um cliente identificado. Escolha o
                cliente antes de usar esta forma de pagamento.
              </LookupNotice>
            ) : credit.blocked ? (
              <p className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)">
                Crédito bloqueado
                {credit.blockReason === null ? '.' : `: ${credit.blockReason}`}
              </p>
            ) : credit.limit === null ? (
              <p className="rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) px-3 py-2 text-(length:--text-xs) text-(--text-primary)">
                {credit.partyName} não tem limite de crédito definido. Crédito é
                concedido, não presumido.
              </p>
            ) : credit.overdueCount > 0 &&
              credit.oldestOverdueDays > credit.overdueToleranceDays &&
              creditRelease === null ? (
              <div className="flex flex-wrap items-center gap-2 rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)">
                <span className="flex-1">
                  {credit.overdueCount === 1
                    ? `1 título vencido há ${String(credit.oldestOverdueDays)} dias.`
                    : `${String(credit.overdueCount)} títulos vencidos, o mais antigo há ${String(credit.oldestOverdueDays)} dias.`}{' '}
                  Receba o que está aberto, ou o gerente libera com a senha.
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setAskingRelease(true)
                  }}
                >
                  Liberar com a senha do gerente
                </Button>
              </div>
            ) : credit.overdueCount > 0 ? (
              <p className="rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) px-3 py-2 text-(length:--text-xs) text-(--text-primary)">
                {credit.overdueCount === 1
                  ? `1 título vencido há ${String(credit.oldestOverdueDays)} dias`
                  : `${String(credit.overdueCount)} títulos vencidos, o mais antigo há ${String(credit.oldestOverdueDays)} dias`}
                {creditRelease === null
                  ? ` — dentro da tolerância de ${String(credit.overdueToleranceDays)} dias.`
                  : ` — liberado por ${creditRelease.by}.`}{' '}
                Disponível: {credit.available}.
              </p>
            ) : (
              <p className="rounded-(--radius-md) border border-(--border-subtle) px-3 py-2 text-(length:--text-xs) text-(--text-secondary)">
                Crédito disponível de {credit.partyName}:{' '}
                <strong className="font-(--font-mono) text-(--text-primary)">
                  {credit.available}
                </strong>{' '}
                (limite {credit.limit}, em aberto {credit.outstanding}).
              </p>
            )
          ) : null}

          <div
            className="flex flex-wrap items-end gap-2 rounded-(--radius-md) border border-(--border-subtle) p-3"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                addTender()
              }
            }}
          >
            <fieldset className="min-w-56 flex-1">
              <legend className="mb-1 text-(length:--text-xs) font-(--weight-medium) text-(--text-secondary)">
                Forma de pagamento
              </legend>
              <div className="flex flex-wrap gap-1.5">
                {methods.map((method, index) => (
                  <button
                    key={method.id}
                    type="button"
                    aria-pressed={method.id === methodId}
                    title={`Alt+${String(index + 1)}`}
                    onClick={() => {
                      setMethodId(method.id)
                    }}
                    className={cn(
                      'flex flex-col items-start rounded-(--radius-md) border px-2.5 py-1.5',
                      'text-left transition-colors duration-(--duration-fast)',
                      method.id === methodId
                        ? 'border-(--brand-primary) bg-(--brand-primary-subtle)'
                        : 'border-(--border-default) hover:border-(--border-strong)'
                    )}
                  >
                    <span className="text-(length:--text-sm) text-(--text-primary)">
                      {method.name}
                    </span>
                    <span className="text-(length:--text-xs) text-(--text-muted)">
                      {index < 9 ? `Alt+${String(index + 1)} · ` : ''}
                      {PAYMENT_METHOD_KIND_LABELS[method.kind]}
                    </span>
                  </button>
                ))}
              </div>
            </fieldset>

            <Field label="Valor recebido" className="w-40">
              {(field) => (
                <MoneyInput
                  {...field}
                  ref={amountRef}
                  value={amountValue}
                  autoFocus

                  onValueChange={setTyped}
                />
              )}
            </Field>

            <Button type="button" variant="secondary" onClick={addTender}>
              Adicionar pagamento
            </Button>
          </div>

          {tenders.length === 0 ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Nenhum pagamento informado.
            </p>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Forma</TableHeaderCell>
                    <TableHeaderCell numeric>Valor</TableHeaderCell>
                    <TableHeaderCell numeric>
                      <span className="sr-only">Ações</span>
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenders.map((tender) => (
                    <TableRow key={tender.key}>
                      <TableCell>{tender.methodName}</TableCell>
                      <TableCell numeric className="font-(--font-mono)">
                        {formatMoney(tender.amount)}
                      </TableCell>
                      <TableCell numeric>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Remover ${tender.methodName}`}
                          onClick={() => {
                            setTenders((current) =>
                              current.filter(
                                (entry) => entry.key !== tender.key
                              )
                            )
                          }}
                        >
                          Remover
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {problem === null && error === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {problem ?? error}
            </p>
          )}

          {/*
            A saída, para quem ainda não sabe como vão pagar (F105).

            Acima do rodapé e não dentro dele: o rodapé é o par
            desistir/concluir desta venda, e isto é uma porta para OUTRO
            documento — um pedido despachado, que sai sem dinheiro e levanta um
            título. Misturá-la com "Concluir venda" faria parecer que existe um
            jeito de concluir sem receber, e não existe: o botão ao lado segue
            desabilitado enquanto faltar dinheiro, byte a byte como a F101 o
            deixou.
          */}
          {onDeliver === undefined ? null : (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-sunken) px-3 py-2">
              <span className="text-(length:--text-sm) text-(--text-secondary)">
                O cliente não vai pagar agora?
              </span>
              {deliveryBlocked === null ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={pending}
                  leadingIcon={<Bike aria-hidden="true" className="h-4 w-4" />}
                  onClick={onDeliver}
                >
                  Mandar para entrega (F10)
                </Button>
              ) : (
                /*
                  Frase, e não botão cinza. Daqui de dentro a pessoa não alcança
                  o F4, então a razão precisa dizer o caminho de volta em vez de
                  apenas informar que não dá.
                */
                <span
                  role="status"
                  className="text-(length:--text-sm) text-(--text-muted)"
                >
                  {deliveryBlocked}
                </span>
              )}
            </div>
          )}

          <DialogFooter className="justify-between">
            <Badge tone={settled ? 'success' : 'warning'}>
              {settled
                ? 'Pronto para concluir'
                : deposit
                  ? 'Informe o sinal'
                  : 'Falta receber'}
            </Badge>

            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>
                Voltar (Esc)
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={pending || !settled}
                onClick={confirm}
              >
                {deposit ? 'Gravar sinal (F2)' : 'Concluir venda (F2)'}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
      {askingRelease && credit !== null ? (
        <AuthorisationDialog
          title="Liberar o fiado com vencido"
          description={`${credit.partyName} tem título vencido. O gerente digita a senha e libera este cliente por alguns minutos; fica registrado quem liberou.`}
          action="finance.credit.manage"
          targetType="party"
          targetId={credit.partyId}
          onAuthorised={(authorisationId, _reason, authorisedBy) => {
            setCreditRelease({ id: authorisationId, by: authorisedBy })
            setAskingRelease(false)
          }}
          onClose={() => {
            setAskingRelease(false)
          }}
        />
      ) : null}
    </Dialog>
  )
}
